import { itemIdOf } from "@aybuben/core/content/types.ts";
import { isCorrect } from "@aybuben/core/exercises/choice/logic.ts";
import type { ChoiceStep } from "@aybuben/core/lesson.ts";
import { useEffect } from "react";

import { useCourse } from "../../content/context.tsx";
import { useStrings } from "../../i18n/index.tsx";
import { G, Stage } from "../../ui/primitives.tsx";
import { useSound, SoundButton } from "../../ui/sound.tsx";
import { SpeakerRow } from "../../ui/SpeakerRow.tsx";
import { OptionGrid, StepHead } from "../kit.tsx";
import { registerView, type ExerciseViewProps, type FeedbackProps } from "../view-registry.ts";

export function ChoiceView({
	step,
	answer,
	setAnswer,
	status,
	retry,
}: ExerciseViewProps<ChoiceStep, string>) {
	const course = useCourse();
	const t = useStrings();
	const sound = useSound();
	// step.answer is always a real letter's base character (see makeChoice), and
	// letterByChar throws rather than returning null, so L is a LetterView —
	// which is why this file needs none of the `L!` assertions the combined
	// component in ui/Lesson.tsx did.
	const L = course.views.letterByChar(step.answer);
	// h2l and s2l prompt with a sound, so
	// it plays on arrival rather than waiting for a tap. playAuto is a no-op
	// until the first pointer gesture (ui/sound.tsx), so first load stays silent
	// and every later step is automatic — the same contract intro/view.tsx
	// relies on. The glyph-prompted modes stay silent: sounding the prompt
	// would give their answer away.
	const spoken = step.mode === "h2l" || step.mode === "s2l";
	const heard = spoken ? course.itemById(itemIdOf(step.key)).audio : undefined;
	useEffect(() => {
		if (heard) sound.playAuto(heard);
	}, [heard, sound]);
	let title: string;
	let prompt;
	if (step.mode === "l2s") {
		title = t("lesson.whatSound");
		prompt = <G size={110}>{L.l}</G>;
	} else if (step.mode === "u2l") {
		title = t("lesson.findLower");
		prompt = <G size={110}>{L.u}</G>;
	} else if (step.mode === "h2l" || step.mode === "s2l") {
		title = t(step.mode === "h2l" ? "lesson.whatHeard" : "lesson.whichLetter");
		prompt = <SpeakerRow asset={heard} status={status} />;
	} else {
		title = t("lesson.findUpper");
		prompt = <G size={110}>{L.l}</G>;
	}
	return (
		<div>
			<StepHead title={title} retry={retry} retryNote={t("lesson.retryNote")} />
			<div className="mt-5">{spoken ? prompt : <Stage>{prompt}</Stage>}</div>
			<OptionGrid
				options={step.options}
				isRight={(id) => isCorrect(step, id)}
				sel={answer}
				status={status}
				onSel={setAnswer}
			/>
		</div>
	);
}

function ChoiceFeedback({ step }: FeedbackProps<ChoiceStep>) {
	const course = useCourse();
	const L = course.views.letterByChar(step.answer);
	const audio = course.itemById(L.id).audio;
	const capsMode = step.mode === "u2l" || step.mode === "l2u";
	return (
		<div className="mt-2 flex items-center gap-3">
			<G size={34}>{capsMode ? `${L.u} ${L.l}` : L.l}</G>
			<div className="text-sm leading-snug">
				<b>{L.r}</b>
				<br />
				{L.h}
			</div>
			{audio && <SoundButton asset={audio} className="ml-auto" />}
		</div>
	);
}

registerView<ChoiceStep, string>("choice", {
	View: ChoiceView,
	Feedback: ChoiceFeedback,
	action: "check",
});
